/**
 * Is the map's style PARSED and safe to mutate?
 *
 * addSource/addLayer/setLayoutProperty/setPaintProperty/setFilter/setGlyphs all call
 * Style._checkLoaded(), which throws "Style is not done loading" while Style._loaded
 * is false (the brief window between map construction and the style JSON parsing).
 * That is the ONLY thing that makes those calls throw.
 *
 * It is tempting to gate on map.isStyleLoaded(), but that is Style.loaded(), which is
 * `_loaded AND every source loaded AND images loaded`. The live position sim setData's
 * a source every tick and the base/seamark tiles stream constantly, so isStyleLoaded()
 * flickers (often stays) false during normal use — gating on it made post-load provider
 * changes silently no-op (the OpenSeaMap base never got added).
 *
 * So we read Style._loaded directly: true once the style is parsed, and it stays true
 * (until a setStyle, which resets it until the new style parses). Property names on the
 * MapLibre instances aren't mangled, so this is stable for the pinned version; a
 * getStyle() fallback keeps it working if the internal ever moves.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';

export function isStyleReady(map: MaplibreMap): boolean {
  const st = (map as unknown as { style?: { _loaded?: boolean } }).style;
  if (st && typeof st._loaded === 'boolean') return st._loaded;
  // Fallback: getStyle() is defined once the style object exists. Slightly looser
  // than _loaded, but only reached if MapLibre renames the internal flag.
  return !!map.getStyle()?.layers;
}
